import { isPlatformBrowser } from '@angular/common';
import {
  Injectable,
  PLATFORM_ID,
  inject,
  signal,
  isDevMode,
} from '@angular/core';
import { Firestore, doc, runTransaction } from '@angular/fire/firestore';
import {
  Messaging,
  getToken,
  onMessage,
  MessagePayload,
} from '@angular/fire/messaging';
import { FcmTokenAngularConverter } from '@padspin/db-ng';
import { fcm_tokens_collection_name } from '@padspin/firestore-fcm-tokens';
import { CurrentUserService } from '@padspin/ui-auth';
import { environment } from '@padspin/ui-environment';
import { SnackbarService } from '@padspin/ui-snackbar';
import { WARN } from '@padspin/ui-logger';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly warn = inject(WARN);
  private readonly messaging = inject(Messaging);
  private readonly firestore = inject(Firestore);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly snackbar = inject(SnackbarService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly fcmTokenConverter = new FcmTokenAngularConverter();
  private foregroundListenerActive = false;

  readonly isPermissionGranted = signal(
    isPlatformBrowser(this.platformId) &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted',
  );

  readonly isPermissionDenied = signal(
    isPlatformBrowser(this.platformId) &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'denied',
  );

  /**
   * Request notification permission and register the FCM token in Firestore.
   * Returns true if permission was granted and token was stored.
   */
  async requestPermissionAndRegister(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    if (typeof Notification === 'undefined') {
      return false;
    }

    const permission = await Notification.requestPermission();
    this.isPermissionGranted.set(permission === 'granted');
    this.isPermissionDenied.set(permission === 'denied');

    if (permission !== 'granted') {
      return false;
    }

    return this.registerTokenAndListen();
  }

  /**
   * Re-register FCM token and start foreground listener on startup.
   * Call this when permission is already granted (e.g., returning user).
   */
  async ensureRegistered(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    if (typeof Notification === 'undefined') {
      return false;
    }
    if (Notification.permission !== 'granted') {
      return false;
    }

    return this.registerTokenAndListen();
  }

  private async registerTokenAndListen(): Promise<boolean> {
    const user = this.currentUserService.currentUser();
    if (!user) {
      return false;
    }

    let token: string;
    try {
      const sw = await this.getServiceWorkerRegistration();
      token = await getToken(this.messaging, {
        vapidKey: environment.vapidKey,
        serviceWorkerRegistration: sw,
      });
    } catch (error) {
      this.warn('Error getting FCM token', error);
      return false;
    }

    if (!token) {
      return false;
    }

    const stored = await this.storeToken(user.uid, token);
    if (!stored) {
      return false;
    }

    this.listenForForegroundMessages();
    return true;
  }

  /**
   * Listen for foreground messages and show them via snackbar.
   * Safe to call multiple times — only registers the listener once.
   */
  listenForForegroundMessages(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.foregroundListenerActive) {
      return;
    }
    this.foregroundListenerActive = true;

    onMessage(this.messaging, (payload) => this.showNotification(payload));
  }

  /**
   * Display a notification in the app via snackbar.
   * Called by `onMessage` for real FCM messages. Can also be called
   * directly in dev mode for testing: `pushService.simulateNotification()`.
   */
  private showNotification(payload: MessagePayload): void {
    const title = payload.notification?.title ?? 'New notification';
    const body = payload.notification?.body ?? '';
    this.snackbar.open(`${title}: ${body}`, {
      severity: 'info',
    });
  }

  /** Simulate a foreground notification for dev testing. */
  simulateNotification(
    title = 'Test Task',
    body = 'This is a test notification',
  ): void {
    if (!isDevMode()) return;
    this.showNotification({ notification: { title, body } } as MessagePayload);
  }

  /**
   * Store a token using a deterministic document ID to prevent duplicates.
   * Uses a transaction to set `created` only on first write.
   */
  private async storeToken(uid: string, token: string): Promise<boolean> {
    const docId = `${uid}_${this.hashToken(token)}`;
    const tokenDocRef = doc(
      this.firestore,
      fcm_tokens_collection_name,
      docId,
    ).withConverter(this.fcmTokenConverter);
    const now = new Date();
    try {
      await runTransaction(this.firestore, async (transaction) => {
        const snap = await transaction.get(tokenDocRef);
        if (snap.exists()) {
          transaction.set(
            tokenDocRef,
            { token, updated: now },
            { merge: true },
          );
        } else {
          transaction.set(tokenDocRef, {
            uid,
            token,
            created: now,
            updated: now,
          });
        }
      });
      return true;
    } catch (error) {
      this.warn('Error storing FCM token', error);
      return false;
    }
  }

  /** Get an existing service worker registration, or register the custom one. */
  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) return existing;
    return navigator.serviceWorker.register('/custom-service-worker.js');
  }

  /** Extract a short, unique suffix from an FCM token for use as a document ID. */
  private hashToken(token: string): string {
    return token.slice(-20);
  }
}
